import { useQuery } from '@tanstack/react-query';
import type { SynchronousQueryConfig } from 'lib/react-query';
import queryString from 'query-string';
import { apiClient } from 'lib/api';
import queryKeys from './queryKeys';
import type { CreativeEntry } from 'types/shared';
import type { UploadDate } from './getCreatives';

export interface CreativesWithHypothesisParams {
  app?: string;
  ad_type?: string;
  network?: string;
  hypothesis?: string[];
  customer?: string;
  flow?: string[];
  technology?: string[];
  status?: string;
  query?: string;
  tpc_finish_date?: string;
  uploaded?: UploadDate;
}

export enum HypothesisKey {
  NoHypothesis = 'No hypothesis',
}

export type CreativesByHypothesis<T extends CreativeEntry = CreativeEntry> = Record<
  string,
  T[]
>;

const toRequestParams = ({
  app,
  uploaded,
  ...filters
}: CreativesWithHypothesisParams): Record<string, unknown> => {
  const activeFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) =>
      Array.isArray(value) ? value.length > 0 : !!value,
    ),
  );

  return {
    app,
    ...activeFilters,
    ...(uploaded && uploaded !== 'all' ? { upload_date: uploaded } : {}),
  };
};

const withRenderableHypotheses = <T extends CreativeEntry>(
  byHypothesis: CreativesByHypothesis<T>,
): CreativesByHypothesis<T> =>
  Object.fromEntries(
    Object.entries(byHypothesis).filter(
      ([hypothesis, creatives]) => !!hypothesis.trim() && creatives.length > 0,
    ),
  );

export const getCreativesWithHypothesis = async <
  T extends CreativeEntry = CreativeEntry,
>(
  { params }: { params: CreativesWithHypothesisParams },
  signal?: AbortSignal,
): Promise<CreativesByHypothesis<T>> => {
  const res = await apiClient.get<CreativesByHypothesis<T>>(
    (queryKeys.allCreativesWithHypothesis as string[]).join('/'),
    {
      params: toRequestParams(params),
      signal,
      paramsSerializer: (prms) =>
        queryString.stringify(prms, { arrayFormat: 'comma' }),
    },
  );
  return withRenderableHypotheses(res.data);
};

/** Merges hypothesis buckets by creative id (later entries win on conflict). */
export const mergeCreativesByHypothesis = <T extends CreativeEntry>(
  ...dictionaries: CreativesByHypothesis<T>[]
): CreativesByHypothesis<T> => {
  const byHypothesis = new Map<string, Map<string, T>>();

  for (const dictionary of dictionaries) {
    for (const [hypothesis, creatives] of Object.entries(dictionary)) {
      const byId = byHypothesis.get(hypothesis) ?? new Map<string, T>();
      for (const creative of creatives) {
        if (creative?.id) {
          byId.set(String(creative.id), creative);
        }
      }
      byHypothesis.set(hypothesis, byId);
    }
  }

  return withRenderableHypotheses(
    Object.fromEntries(
      [...byHypothesis.entries()].map(([hypothesis, byId]) => [
        hypothesis,
        [...byId.values()],
      ]),
    ),
  );
};

/**
 * Deep-link helper for hypothesis testing when the URL already has a `search`
 * value (with or without packs). Loads the filtered list and the default
 * browse list in parallel, then merges them (deduped by creative id) so pack
 * validation and the default query cache both see search matches.
 */
export const fetchCreativesWithHypothesisSearchMerge = async <
  T extends CreativeEntry = CreativeEntry,
>(
  {
    params,
    searchQuery,
  }: {
    params: CreativesWithHypothesisParams;
    searchQuery?: string;
  },
  signal?: AbortSignal,
): Promise<CreativesByHypothesis<T>> => {
  const trimmedSearch = searchQuery?.trim();
  const baseParams: CreativesWithHypothesisParams = { ...params };
  delete baseParams.query;

  if (!trimmedSearch) {
    return getCreativesWithHypothesis<T>({ params: baseParams }, signal);
  }

  const [matchedBySearch, baseList] = await Promise.all([
    getCreativesWithHypothesis<T>(
      { params: { ...baseParams, query: trimmedSearch } },
      signal,
    ),
    getCreativesWithHypothesis<T>({ params: baseParams }, signal),
  ]);

  return mergeCreativesByHypothesis(baseList, matchedBySearch);
};

interface UseCreativesWithHypothesisOptions<T extends CreativeEntry> {
  params: CreativesWithHypothesisParams;
  config?: SynchronousQueryConfig<
    typeof getCreativesWithHypothesis<T>,
    CreativesByHypothesis<T>
  >;
}

export const useCreativesWithHypothesis = <T extends CreativeEntry = CreativeEntry>({
  params,
  config,
}: UseCreativesWithHypothesisOptions<T>) =>
  useQuery({
    staleTime: 0,
    refetchOnMount: true,
    ...config,
    queryFn: ({ signal }) => getCreativesWithHypothesis<T>({ params }, signal),
    queryKey: queryKeys.creativesWithHypothesis<CreativesWithHypothesisParams>(params),
  });
